import { LedgerError } from './errors';
import { formatMinor } from './money';
import { computeRevenueSplit, type RevenueSplit } from './split';

/**
 * The portion of an order line being returned to the player.
 *
 * Supplied explicitly rather than derived, because the three components do not
 * move together: tax is recoverable from the authority, and whether the
 * processor returns its fee is a processor question, not an arithmetic one.
 */
export interface RefundAmounts {
  readonly refundedPrice: number;
  readonly refundedTax: number;
  readonly refundedFees: number;
}

export const NOTHING_REFUNDED: RefundAmounts = Object.freeze({
  refundedPrice: 0,
  refundedTax: 0,
  refundedFees: 0,
});

export interface RefundInput extends RefundAmounts {
  readonly orderLineId: string;
  /** Identifies this refund event. Two partial refunds on one line have distinct ids. */
  readonly refundId: string;
  /** The split written at sale time. Carries the rates this refund must use. */
  readonly originalSplit: RevenueSplit;
  readonly creatorId: string | null;
  /**
   * Every refund already applied to this line, summed. Zero for the first.
   * Held by the caller as a sum over prior refund rows, never as a stored
   * running total (hard rule 2).
   */
  readonly alreadyRefunded?: RefundAmounts;
  /**
   * How much of the reversed processor fee the processor actually gave back.
   *
   * Defaults to zero, which is Stripe's behaviour: the fee on the original
   * payment is not refunded. Whatever is not returned becomes a GOODBUILD
   * expense rather than being clawed back from the creator.
   */
  readonly processorFeesReturned?: number;
}

function addAmounts(a: RefundAmounts, b: RefundAmounts): RefundAmounts {
  return {
    refundedPrice: a.refundedPrice + b.refundedPrice,
    refundedTax: a.refundedTax + b.refundedTax,
    refundedFees: a.refundedFees + b.refundedFees,
  };
}

function splitOf(originalSplit: RevenueSplit, amounts: RefundAmounts): RevenueSplit {
  return computeRevenueSplit({
    currency: originalSplit.currency,
    pricePaid: amounts.refundedPrice,
    tax: amounts.refundedTax,
    paymentFees: amounts.refundedFees,
    creatorRateBps: originalSplit.creatorRateBps,
    platformRateBps: originalSplit.platformRateBps,
  });
}

/** The six amounts a refund journal moves, all non-negative. */
export interface RefundAllocation {
  readonly price: number;
  readonly tax: number;
  readonly paymentFees: number;
  readonly commission: number;
  readonly developerNet: number;
  readonly platformGross: number;
}

/**
 * What this refund event reverses, computed as a difference of cumulative
 * allocations rather than an allocation of this event in isolation.
 *
 * This is the same principle the waterfall engine runs on: derive cumulative
 * state, take the delta, never accumulate rounded increments. Allocating each
 * partial refund independently lets rounding compound, so five one-cent
 * refunds against a five-cent line can reverse more commission than the sale
 * ever credited. Taking the difference of two exact cumulative splits makes
 * the total reversed always equal the split of the total refunded, and
 * therefore exactly equal to the original split when a line is fully refunded.
 */
export function computeRefundAllocation(input: RefundInput): RefundAllocation {
  const { originalSplit } = input;
  const prior = input.alreadyRefunded ?? NOTHING_REFUNDED;

  const thisEvent: RefundAmounts = {
    refundedPrice: input.refundedPrice,
    refundedTax: input.refundedTax,
    refundedFees: input.refundedFees,
  };

  for (const [label, value] of Object.entries(thisEvent)) {
    if (value < 0) {
      throw new LedgerError(`${label} must not be negative on a refund, got ${value}`);
    }
  }

  const cumulative = addAmounts(prior, thisEvent);
  const after = assertWithinOriginal(input, cumulative);
  const before = splitOf(originalSplit, prior);

  return Object.freeze({
    price: after.pricePaid - before.pricePaid,
    tax: after.tax - before.tax,
    paymentFees: after.paymentFees - before.paymentFees,
    commission: after.commission - before.commission,
    developerNet: after.developerNet - before.developerNet,
    platformGross: after.platformGross - before.platformGross,
  });
}

/**
 * A refund may never reverse more than the sale took, on any component.
 *
 * Both the amounts refunded and everything derived from them are checked. The
 * derived check is the one that matters: the three inputs do not move together,
 * so a refund can stay inside the original on every input and still exceed it
 * on commission. Refunding the full price while passing zero payment fees,
 * because the processor kept its fee, raises the refunded net above the
 * original net and would claw back more commission than the creator was ever
 * credited. That throws here rather than quietly happening.
 *
 * Who absorbs a retained processor fee is a policy decision. This makes the
 * absence of that decision loud instead of silent.
 *
 * Returns the validated cumulative split so the caller does not recompute it.
 */
export function assertWithinOriginal(input: RefundInput, cumulative: RefundAmounts): RevenueSplit {
  const { originalSplit, orderLineId } = input;
  const { currency } = originalSplit;
  const cumulativeSplit = splitOf(originalSplit, cumulative);

  const checks: { label: string; refunded: number; original: number }[] = [
    { label: 'price', refunded: cumulative.refundedPrice, original: originalSplit.pricePaid },
    { label: 'tax', refunded: cumulative.refundedTax, original: originalSplit.tax },
    {
      label: 'payment fees',
      refunded: cumulative.refundedFees,
      original: originalSplit.paymentFees,
    },
    { label: 'net', refunded: cumulativeSplit.net, original: originalSplit.net },
    {
      label: 'commission',
      refunded: cumulativeSplit.commission,
      original: originalSplit.commission,
    },
    {
      label: 'developer net',
      refunded: cumulativeSplit.developerNet,
      original: originalSplit.developerNet,
    },
    {
      label: 'platform gross',
      refunded: cumulativeSplit.platformGross,
      original: originalSplit.platformGross,
    },
  ];

  for (const check of checks) {
    if (check.refunded > check.original) {
      throw new LedgerError(
        `Refunds on order line ${orderLineId} would reverse ${check.label} totalling ` +
          `${formatMinor(currency, check.refunded)} against an original of ` +
          `${formatMinor(currency, check.original)}. A refund cannot return more than was taken.`,
      );
    }
  }

  return cumulativeSplit;
}

/*
 * `buildRefundJournal` and `refundIdempotencyKey` were here, and the ledger
 * removal took them.
 *
 * They turned the allocation below into a reversing double-entry journal, every
 * line of the sale journal with its direction flipped. There are no journals
 * any more — Creem is the record of money — so what is left in this file is the
 * part that was never bookkeeping: working out how much of the price, the tax,
 * the fees and the creator's commission a partial refund actually returns.
 *
 * `refund-order.ts` still calls `computeRefundAllocation` and
 * `assertWithinOriginal` for exactly that, and writes the result to the
 * `refund` table, which is where refunds-to-date have always been summed from.
 */

/** Convenience for the common case: the whole line comes back. */
export function fullRefundAmounts(originalSplit: RevenueSplit): RefundAmounts {
  return Object.freeze({
    refundedPrice: originalSplit.pricePaid,
    refundedTax: originalSplit.tax,
    refundedFees: originalSplit.paymentFees,
  });
}
